import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  TablePagination,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  AccountBalance,
  Inventory,
  TrendingUp,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { inventoryReportsService, StockLevelDto } from '../../services/inventoryReports.service';
import { Warehouse } from '../../types/warehouses.types';
import { warehousesService } from '../../services/warehouses.service';
import { Branch } from '../../types/branches.types';
import { branchesService } from '../../services/branches.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { fmtUnitPrice } from '../../utils/formatNumber';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const translations = {
  en: {
    title: 'Inventory Valuation Report',
    titleAr: 'تقرير تقييم المخزون',
    detailedItems: 'Detailed Items',
    byCategory: 'By Category',
    byWarehouse: 'By Warehouse',
    byBranch: 'By Branch',
    byLocation: 'Location Summary',
    methodComparison: 'Method Comparison',
    warehouse: 'Warehouse',
    branch: 'Branch',
    allWarehouses: 'All Warehouses',
    allBranches: 'All Branches',
    allLocations: 'All Locations',
    warehousesOnly: 'Warehouses Only',
    branchesOnly: 'Branches Only',
    locationType: 'Location Type',
    valuationMethod: 'Valuation Method',
    fifo: 'FIFO',
    lifo: 'LIFO',
    average: 'Weighted Average',
    itemCode: 'Item Code',
    itemName: 'Item',
    categoryName: 'Category',
    warehouseName: 'Warehouse',
    branchName: 'Branch',
    quantity: 'Qty',
    unitCost: 'Unit Cost',
    totalValue: 'Total Value',
    fifoValue: 'FIFO Value',
    lifoValue: 'LIFO Value',
    avgValue: 'AVG Value',
    totalInventory: 'Total Inventory',
    totalItems: 'Total Items',
    totalQuantity: 'Total Quantity',
    avgCost: 'Average Cost',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    noData: 'No inventory data',
    loading: 'Loading...',
    kwd: 'KWD',
    asOf: 'As of',
    balanceSheet: 'For Balance Sheet',
    itemsCount: 'Items',
    filters: 'Filters',
  },
  ar: {
    title: 'تقرير تقييم المخزون',
    titleAr: 'تقرير تقييم المخزون',
    detailedItems: 'الأصناف التفصيلية',
    byCategory: 'حسب الفئة',
    byWarehouse: 'حسب المخزن',
    byBranch: 'حسب الفرع',
    byLocation: 'ملخص المواقع',
    methodComparison: 'مقارنة الطرق',
    warehouse: 'المخزن',
    branch: 'الفرع',
    allWarehouses: 'جميع المخازن',
    allBranches: 'جميع الفروع',
    allLocations: 'جميع المواقع',
    warehousesOnly: 'المخازن فقط',
    branchesOnly: 'الفروع فقط',
    locationType: 'نوع الموقع',
    valuationMethod: 'طريقة التقييم',
    fifo: 'الوارد أولاً صادر أولاً',
    lifo: 'الوارد أخيراً صادر أولاً',
    average: 'المتوسط المرجح',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    categoryName: 'الفئة',
    warehouseName: 'المخزن',
    branchName: 'الفرع',
    quantity: 'الكمية',
    unitCost: 'سعر الوحدة',
    totalValue: 'القيمة الإجمالية',
    fifoValue: 'قيمة FIFO',
    lifoValue: 'قيمة LIFO',
    avgValue: 'قيمة المتوسط',
    totalInventory: 'إجمالي المخزون',
    totalItems: 'إجمالي الأصناف',
    totalQuantity: 'إجمالي الكمية',
    avgCost: 'متوسط التكلفة',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    noData: 'لا توجد بيانات مخزون',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
    asOf: 'كما في',
    balanceSheet: 'للميزانية العمومية',
    itemsCount: 'صنف',
    filters: 'الفلاتر',
  },
};

interface ItemValuation {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  categoryId: number;
  categoryName: string;
  categoryNameAr: string;
  warehouseId?: number;
  warehouseName: string;
  warehouseNameAr: string;
  branchId?: number;
  branchName: string;
  branchNameAr: string;
  quantity: number;
  fifoValue: number;
  lifoValue: number;
  avgValue: number;
  fifoCost: number;
  lifoCost: number;
  avgCost: number;
}

interface CategorySummary {
  categoryId: number;
  categoryName: string;
  categoryNameAr: string;
  itemsCount: number;
  totalQuantity: number;
  fifoValue: number;
  lifoValue: number;
  avgValue: number;
}

interface WarehouseSummary {
  warehouseId?: number;
  warehouseName: string;
  warehouseNameAr: string;
  itemsCount: number;
  totalQuantity: number;
  fifoValue: number;
  lifoValue: number;
  avgValue: number;
}

const InventoryValuationReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const [stockLevels, setStockLevels] = useState<StockLevelDto[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [branchFilter, setBranchFilter] = useState<number | ''>('');
  const [valuationMethod, setValuationMethod] = useState<'FIFO' | 'LIFO' | 'AVG'>('FIFO');
  const [locationType, setLocationType] = useState<'all' | 'warehouse' | 'branch'>('all');
  const [detailPage, setDetailPage] = useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(25);

  useEffect(() => {
    loadWarehouses();
    loadBranches();
    loadCompanyInfo();
    loadData();
  }, []);

  useEffect(() => {
    if (warehouseFilter || branchFilter) {
      loadData();
    }
  }, [warehouseFilter, branchFilter]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      const data = await warehousesService.getAll();
      setWarehouses(data);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await branchesService.getAll();
      setBranches(data);
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await inventoryReportsService.getStockLevels({
        warehouseId: warehouseFilter || undefined,
        branchId: branchFilter || undefined,
        pageSize: 1000,
      });
      setStockLevels(result.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cost is now calculated by the backend API from StockMovements (supports Opening, Purchase, Transfer, etc.)

  const getItemValuations = (): ItemValuation[] => {
    const items: ItemValuation[] = [];
    
    stockLevels.forEach(stock => {
      if ((stock.quantity || 0) > 0) {
        const quantity = stock.quantity || 0;
        const unitCost = stock.unitCost || 0;
        const totalValue = stock.totalValue || (quantity * unitCost);

        items.push({
          itemId: stock.itemId,
          itemCode: stock.itemCode,
          itemName: stock.itemName,
          itemNameAr: stock.itemNameAr,
          categoryId: 0,
          categoryName: '',
          categoryNameAr: '',
          warehouseId: stock.warehouseId,
          warehouseName: isRTL ? (stock.warehouseNameAr || stock.warehouseName || '-') : (stock.warehouseName || '-'),
          warehouseNameAr: stock.warehouseNameAr || '-',
          branchId: stock.branchId,
          branchName: isRTL ? (stock.branchNameAr || stock.branchName || '-') : (stock.branchName || '-'),
          branchNameAr: stock.branchNameAr || '-',
          quantity,
          fifoValue: totalValue,
          lifoValue: totalValue,
          avgValue: totalValue,
          fifoCost: unitCost,
          lifoCost: unitCost,
          avgCost: unitCost,
        });
      }
    });

    return items;
  };

  const getFilteredItems = (): ItemValuation[] => {
    let items = getItemValuations();
    
    // Apply location type filter
    if (locationType === 'warehouse') {
      items = items.filter(i => i.warehouseId && !i.branchId);
    } else if (locationType === 'branch') {
      items = items.filter(i => i.branchId && !i.warehouseId);
    }
    
    return items;
  };

  const getCategorySummary = (): CategorySummary[] => {
    const summary = new Map<number, CategorySummary>();
    const items = getFilteredItems();

    items.forEach(item => {
      if (!summary.has(item.categoryId)) {
        summary.set(item.categoryId, {
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          categoryNameAr: item.categoryNameAr,
          itemsCount: 0,
          totalQuantity: 0,
          fifoValue: 0,
          lifoValue: 0,
          avgValue: 0,
        });
      }

      const cat = summary.get(item.categoryId)!;
      cat.itemsCount++;
      cat.totalQuantity += item.quantity;
      cat.fifoValue += item.fifoValue;
      cat.lifoValue += item.lifoValue;
      cat.avgValue += item.avgValue;
    });

    return Array.from(summary.values()).sort((a, b) => b.fifoValue - a.fifoValue);
  };

  const getWarehouseSummary = (): WarehouseSummary[] => {
    const summary = new Map<number, WarehouseSummary>();
    const items = getFilteredItems();

    items.forEach(item => {
      const whId = item.warehouseId || 0;
      if (!summary.has(whId)) {
        summary.set(whId, {
          warehouseId: item.warehouseId,
          warehouseName: item.warehouseName,
          warehouseNameAr: item.warehouseNameAr,
          itemsCount: 0,
          totalQuantity: 0,
          fifoValue: 0,
          lifoValue: 0,
          avgValue: 0,
        });
      }

      const wh = summary.get(whId)!;
      wh.itemsCount++;
      wh.totalQuantity += item.quantity;
      wh.fifoValue += item.fifoValue;
      wh.lifoValue += item.lifoValue;
      wh.avgValue += item.avgValue;
    });

    return Array.from(summary.values()).sort((a, b) => b.fifoValue - a.fifoValue);
  };

  const getBranchSummary = (): WarehouseSummary[] => {
    const summary = new Map<number, WarehouseSummary>();
    const items = getFilteredItems();

    items.forEach(item => {
      if (item.branchId) {
        if (!summary.has(item.branchId)) {
          summary.set(item.branchId, {
            warehouseId: item.branchId,
            warehouseName: item.branchName,
            warehouseNameAr: item.branchNameAr,
            itemsCount: 0,
            totalQuantity: 0,
            fifoValue: 0,
            lifoValue: 0,
            avgValue: 0,
          });
        }

        const br = summary.get(item.branchId)!;
        br.itemsCount++;
        br.totalQuantity += item.quantity;
        br.fifoValue += item.fifoValue;
        br.lifoValue += item.lifoValue;
        br.avgValue += item.avgValue;
      }
    });

    return Array.from(summary.values()).sort((a, b) => b.fifoValue - a.fifoValue);
  };

  const getAllLocationsSummary = (): (WarehouseSummary & { locationType: string })[] => {
    const warehouseSummary = getWarehouseSummary().map(w => ({ ...w, locationType: 'Warehouse' }));
    const branchSummary = getBranchSummary().map(b => ({ ...b, locationType: 'Branch' }));
    
    return [...warehouseSummary, ...branchSummary].sort((a, b) => b.fifoValue - a.fifoValue);
  };

  const getStatistics = () => {
    const items = getFilteredItems();
    
    return {
      totalItems: items.length,
      totalQuantity: items.reduce((sum, i) => sum + i.quantity, 0),
      fifoValue: items.reduce((sum, i) => sum + i.fifoValue, 0),
      lifoValue: items.reduce((sum, i) => sum + i.lifoValue, 0),
      avgValue: items.reduce((sum, i) => sum + i.avgValue, 0),
    };
  };

  const formatCurrency = (amount: number) => {
    return amount.toFixed(3);
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 80 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      worksheet.mergeCells('C1:K1');
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FF1565C0' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      // معلومات الشركة
      worksheet.mergeCells('C2:K2');
      const addressCell = worksheet.getCell('C2');
      addressCell.value = `📍 ${companyInfo?.address || ''}`;
      addressCell.font = { size: 10, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('C3:K3');
      const contactCell = worksheet.getCell('C3');
      contactCell.value = `📞 ${companyInfo?.phone || ''} | ✉️ ${companyInfo?.email || ''}`;
      contactCell.font = { size: 10, color: { argb: 'FF424242' } };
      contactCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('A5:K5');
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1565C0' }
      };

      const stats = getStatistics();
      worksheet.mergeCells('A6:K6');
      const statsCell = worksheet.getCell('A6');
      statsCell.value = `${t.asOf}: ${new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')} | FIFO: ${formatCurrency(stats.fifoValue)} | LIFO: ${formatCurrency(stats.lifoValue)} | AVG: ${formatCurrency(stats.avgValue)}`;
      statsCell.font = { size: 11, bold: true, color: { argb: 'FF1565C0' } };
      statsCell.alignment = { horizontal: 'center' };

      // إضافة الفلاتر المطبقة
      let filterText = '';
      if (warehouseFilter) {
        const wh = warehouses.find(w => w.warehouseId === warehouseFilter);
        filterText += `${t.warehouse}: ${isRTL ? wh?.warehouseNameAr : wh?.warehouseName}`;
      }
      if (branchFilter) {
        const br = branches.find(b => b.branchId === branchFilter);
        if (filterText) filterText += ' | ';
        filterText += `${t.branch}: ${isRTL ? br?.branchNameAr : br?.branchName}`;
      }
      if (filterText) {
        worksheet.mergeCells('A7:K7');
        const filterCell = worksheet.getCell('A7');
        filterCell.value = `🔍 ${filterText}`;
        filterCell.font = { size: 10, bold: true, color: { argb: 'FF000000' } };
        filterCell.alignment = { horizontal: 'center' };
        filterCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF5F5F5' }
        };
      }

      const data = getFilteredItems();
      const headers = [
        t.itemCode, t.itemName, t.warehouseName, t.quantity,
        t.fifoValue, t.lifoValue, t.avgValue
      ];
      
      const headerRow = worksheet.getRow(filterText ? 9 : 8);
      headers.forEach((header, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1565C0' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      
      let currentRow = filterText ? 10 : 9;
      data.forEach((item, index) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = item.itemCode;
        row.getCell(2).value = isRTL ? item.itemNameAr : item.itemName;
        row.getCell(3).value = item.warehouseName;
        row.getCell(4).value = item.quantity;
        row.getCell(5).value = item.fifoValue;
        row.getCell(6).value = item.lifoValue;
        row.getCell(7).value = item.avgValue;
        
        if (index % 2 === 0) {
          row.eachCell(cell => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF5F5F5' }
            };
          });
        }
        
        currentRow++;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `InventoryValuation_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;

      doc.setFillColor(245, 250, 255);
      doc.rect(0, 0, pageWidth, 55, 'F');

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(21, 101, 192);
      doc.text(isRTL ? (companyInfo?.companyNameAr || '') : (companyInfo?.companyName || ''), pageWidth / 2, 15, { align: 'center' });

      // معلومات الشركة
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(66, 66, 66);
      doc.text(`📍 ${companyInfo?.address || ''}`, pageWidth / 2, 21, { align: 'center' });
      doc.text(`📞 ${companyInfo?.phone || ''} | ✉️ ${companyInfo?.email || ''}`, pageWidth / 2, 26, { align: 'center' });

      // عنوان التقرير
      doc.setFillColor(21, 101, 192);
      doc.roundedRect(pageWidth / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(isRTL ? t.titleAr : t.title, pageWidth / 2, 38.5, { align: 'center' });

      // الفلاتر المطبقة
      let startY = 48;
      if (warehouseFilter || branchFilter) {
        doc.setFillColor(245, 245, 245);
        doc.rect(10, 45, pageWidth - 20, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        
        let filterText = '🔍 ';
        if (warehouseFilter) {
          const wh = warehouses.find(w => w.warehouseId === warehouseFilter);
          filterText += `${t.warehouse}: ${isRTL ? wh?.warehouseNameAr : wh?.warehouseName}`;
        }
        if (branchFilter) {
          const br = branches.find(b => b.branchId === branchFilter);
          if (warehouseFilter) filterText += ' | ';
          filterText += `${t.branch}: ${isRTL ? br?.branchNameAr : br?.branchName}`;
        }
        
        doc.text(filterText, pageWidth / 2, 50, { align: 'center' });
        startY = 58;
      }

      const tableHeaders = [
        t.itemName,
        t.quantity,
        t.fifoValue,
        t.lifoValue,
        t.avgValue,
      ];

      const tableData: any[] = [];
      const data = getFilteredItems();

      data.forEach((item) => {
        tableData.push([
          (isRTL ? item.itemNameAr : item.itemName).substring(0, 30),
          formatCurrency(item.quantity),
          formatCurrency(item.fifoValue),
          formatCurrency(item.lifoValue),
          formatCurrency(item.avgValue),
        ]);
      });

      const stats = getStatistics();
      tableData.push([
        {
          content: 'TOTAL',
          colSpan: 2,
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 11,
            halign: 'center'
          }
        },
        {
          content: formatCurrency(stats.fifoValue),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        },
        {
          content: formatCurrency(stats.lifoValue),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        },
        {
          content: formatCurrency(stats.avgValue),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        }
      ]);

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: startY,
        theme: 'striped',
        headStyles: {
          fillColor: [21, 101, 192],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50]
        },
      });

      doc.save(`InventoryValuation_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const stats = getStatistics();

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ 
          background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 'bold'
        }}>
          {t.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<ExcelIcon />} onClick={exportToExcel}
            sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white' }}>
            {t.exportExcel}
          </Button>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={exportToPdf}
            sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white' }}>
            {t.exportPdf}
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
            sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white' }}>
            {t.print}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }} className="no-print">
        <Card sx={{ background: 'linear-gradient(135deg, #1976D2 0%, #42A5F5 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.fifo}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.fifoValue)}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t.kwd}</Typography>
              </Box>
              <AccountBalance sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #7B1FA2 0%, #BA68C8 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.lifo}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.lifoValue)}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t.kwd}</Typography>
              </Box>
              <TrendingUp sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #00897B 0%, #4DB6AC 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.average}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.avgValue)}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t.kwd}</Typography>
              </Box>
              <Inventory sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #F57C00 0%, #FFB74D 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalItems}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.totalItems}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t.itemsCount}</Typography>
              </Box>
              <CategoryIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>Filters</Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))].find(o => o.id === warehouseFilter) || { id: '' as number | '', label: t.allWarehouses }}
            onChange={(_, v) => { setWarehouseFilter(v?.id || ''); setDetailPage(0); }}
            renderInput={(params) => <TextField {...params} label={t.warehouse} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))].find(o => o.id === branchFilter) || { id: '' as number | '', label: t.allBranches }}
            onChange={(_, v) => { setBranchFilter(v?.id || ''); setDetailPage(0); }}
            renderInput={(params) => <TextField {...params} label={t.branch} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <TextField select fullWidth size="small" label={t.locationType} value={locationType}
            onChange={(e) => { setLocationType(e.target.value as 'all' | 'warehouse' | 'branch'); setDetailPage(0); }}>
            <MenuItem value="all">{t.allLocations}</MenuItem>
            <MenuItem value="warehouse">{t.warehousesOnly}</MenuItem>
            <MenuItem value="branch">{t.branchesOnly}</MenuItem>
          </TextField>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>{t.valuationMethod}</Typography>
            <ToggleButtonGroup
              value={valuationMethod}
              exclusive
              onChange={(_, newValue) => { if (newValue) { setValuationMethod(newValue); setDetailPage(0); } }}
              fullWidth
              size="small"
            >
              <ToggleButton value="FIFO">FIFO</ToggleButton>
              <ToggleButton value="LIFO">LIFO</ToggleButton>
              <ToggleButton value="AVG">AVG</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Button variant="contained" onClick={loadData} fullWidth
          sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)', color: 'white' }}>
          {t.refresh}
        </Button>
      </Paper>

      <Paper sx={{ mb: 2 }} className="no-print">
        <Tabs value={currentTab} onChange={(_, newValue) => { setCurrentTab(newValue); setDetailPage(0); }} variant="scrollable" scrollButtons="auto">
          <Tab label={t.detailedItems} />
          <Tab label={t.byWarehouse} />
          <Tab label={t.byBranch} />
          <Tab label={t.byLocation} />
          <Tab label={t.methodComparison} />
        </Tabs>
      </Paper>

      {(warehouseFilter || branchFilter) && (
        <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)', border: '2px solid #1565C0' }} className="no-print">
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1565C0', mb: 1 }}>
            🔍 {t.filters}:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {warehouseFilter && (
              <Chip 
                label={`${t.warehouse}: ${warehouses.find(w => w.warehouseId === warehouseFilter)?.[isRTL ? 'warehouseNameAr' : 'warehouseName']}`}
                color="primary"
                sx={{ fontWeight: 'bold' }}
              />
            )}
            {branchFilter && (
              <Chip 
                label={`${t.branch}: ${branches.find(b => b.branchId === branchFilter)?.[isRTL ? 'branchNameAr' : 'branchName']}`}
                color="secondary"
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Box>
        </Paper>
      )}

      <Box className="print-only">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 2, borderBottom: '3px solid #3F51B5' }}>
          {companyInfo?.logoBase64 && (
            <Box sx={{ width: '100px' }}>
              <img src={companyInfo.logoBase64} alt="Logo" style={{ height: '60px', maxWidth: '100px', objectFit: 'contain' }} />
            </Box>
          )}
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography sx={{ fontSize: '22px', fontWeight: 'bold', color: '#3F51B5', mb: 0.5 }}>
              {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#424242', mb: 0.3 }}>
              📍 {companyInfo?.address || ''}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#424242' }}>
              📞 {companyInfo?.phone || ''} | 📧 {companyInfo?.email || ''}
            </Typography>
          </Box>
          <Box sx={{ width: '100px' }}></Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 2, py: 1, background: '#3F51B5', color: 'white', borderRadius: '4px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
            {isRTL ? t.titleAr : t.title}
          </Typography>
          <Typography sx={{ fontSize: '10px', mt: 0.5 }}>
            {t.asOf}: {new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2, p: 2, background: '#F5F5F5', border: '2px solid #3F51B5', borderRadius: '4px' }}>
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 'bold', color: '#3F51B5', mb: 1 }}>
              📊 {t.valuationMethod}:
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#000000' }}>
              • FIFO: {formatCurrency(stats.fifoValue)} {t.kwd}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#000000' }}>
              • LIFO: {formatCurrency(stats.lifoValue)} {t.kwd}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#000000' }}>
              • AVG: {formatCurrency(stats.avgValue)} {t.kwd}
            </Typography>
          </Box>
          
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 'bold', color: '#3F51B5', mb: 1 }}>
              🔍 {t.filters}:
            </Typography>
            {warehouseFilter && (
              <Typography sx={{ fontSize: '10px', color: '#000000' }}>
                • {t.warehouse}: {warehouses.find(w => w.warehouseId === warehouseFilter)?.[isRTL ? 'warehouseNameAr' : 'warehouseName']}
              </Typography>
            )}
            {branchFilter && (
              <Typography sx={{ fontSize: '10px', color: '#000000' }}>
                • {t.branch}: {branches.find(b => b.branchId === branchFilter)?.[isRTL ? 'branchNameAr' : 'branchName']}
              </Typography>
            )}
            {!warehouseFilter && !branchFilter && (
              <Typography sx={{ fontSize: '10px', color: '#000000' }}>
                • {locationType === 'all' ? t.allLocations : locationType === 'warehouse' ? t.warehousesOnly : t.branchesOnly}
              </Typography>
            )}
            <Typography sx={{ fontSize: '10px', color: '#000000', mt: 0.5 }}>
              • {t.totalItems}: {stats.totalItems}
            </Typography>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          {currentTab === 0 && (
            <>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.quantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.unitCost}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredItems().slice(detailPage * detailRowsPerPage, detailPage * detailRowsPerPage + detailRowsPerPage).map((row, index) => {
                  const value = valuationMethod === 'FIFO' ? row.fifoValue :
                               valuationMethod === 'LIFO' ? row.lifoValue : row.avgValue;
                  const cost = valuationMethod === 'FIFO' ? row.fifoCost : 
                              valuationMethod === 'LIFO' ? row.lifoCost : row.avgCost;

                  return (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' }, backgroundColor: index % 2 === 0 ? 'background.default' : 'action.hover' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                      </TableCell>
                      <TableCell>{row.warehouseName}</TableCell>
                      <TableCell align="right">{formatCurrency(row.quantity)}</TableCell>
                      <TableCell align="right">{fmtUnitPrice(cost)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(value)}</TableCell>
                    </TableRow>
                  );
                })}
                {getFilteredItems().length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}><Typography color="text.secondary">{t.noData}</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={getFilteredItems().length}
              page={detailPage}
              onPageChange={(_, p) => setDetailPage(p)}
              rowsPerPage={detailRowsPerPage}
              onRowsPerPageChange={(e) => { setDetailRowsPerPage(parseInt(e.target.value, 10)); setDetailPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
            </>
          )}

          {currentTab === 1 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalItems}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.fifoValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.lifoValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.avgValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getWarehouseSummary().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>{isRTL ? row.warehouseNameAr : row.warehouseName}</TableCell>
                    <TableCell align="right">{row.itemsCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1976D2' }}>{formatCurrency(row.fifoValue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#7B1FA2' }}>{formatCurrency(row.lifoValue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#00897B' }}>{formatCurrency(row.avgValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 2 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.branchName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalItems}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.fifoValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.lifoValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.avgValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getBranchSummary().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>{isRTL ? row.warehouseNameAr : row.warehouseName}</TableCell>
                    <TableCell align="right">{row.itemsCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1976D2' }}>{formatCurrency(row.fifoValue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#7B1FA2' }}>{formatCurrency(row.lifoValue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#00897B' }}>{formatCurrency(row.avgValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 3 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Location</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalItems}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.fifoValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.lifoValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.avgValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getAllLocationsSummary().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      <Chip 
                        label={row.locationType} 
                        size="small" 
                        color={row.locationType === 'Warehouse' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: '500' }}>{isRTL ? row.warehouseNameAr : row.warehouseName}</TableCell>
                    <TableCell align="right">{row.itemsCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1976D2' }}>{formatCurrency(row.fifoValue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#7B1FA2' }}>{formatCurrency(row.lifoValue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#00897B' }}>{formatCurrency(row.avgValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 4 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.quantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.fifoValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.lifoValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.avgValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredItems().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.quantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1976D2' }}>{formatCurrency(row.fifoValue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#7B1FA2' }}>{formatCurrency(row.lifoValue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#00897B' }}>{formatCurrency(row.avgValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      <style>{`
        @media print {
          /* إخفاء عناصر غير ضرورية */
          header, nav, .MuiAppBar-root, .MuiDrawer-root, .MuiToolbar-root { display: none !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          
          /* إعدادات الصفحة */
          @page { 
            margin: 1.5cm 1cm; 
            size: landscape; 
          }
          
          * { 
            orphans: 3; 
            widows: 3; 
          }
          
          body { 
            font-size: 10px;
            color: #000000 !important;
            background: white !important;
          }
          
          main, .MuiBox-root { 
            margin: 0 !important; 
            padding: 10px !important; 
            max-width: 100% !important; 
          }
          
          /* تنسيق الجداول للطباعة */
          table { 
            page-break-after: avoid;
            page-break-inside: auto;
            border-collapse: collapse !important;
            width: 100%;
          }
          
          table tr { 
            page-break-inside: avoid; 
            page-break-after: auto; 
          }
          
          /* رأس الجدول - خلفية رمادية فاتحة */
          table thead tr { 
            background: #E8EAF6 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          table thead th { 
            background: #E8EAF6 !important;
            color: #000000 !important;
            font-weight: bold !important;
            padding: 8px 6px !important;
            border: 2px solid #3F51B5 !important;
            font-size: 10px !important;
            text-align: center !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          /* صفوف الجدول - بدون خلفية داكنة */
          table tbody tr { 
            background: white !important;
          }
          
          table tbody tr:nth-child(even) { 
            background: #F5F5F5 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          table tbody td { 
            padding: 6px 8px !important;
            border: 1px solid #BDBDBD !important;
            color: #000000 !important;
            font-size: 9px !important;
            line-height: 1.4 !important;
          }
          
          table tbody td .MuiTypography-caption { 
            color: #424242 !important;
            opacity: 1 !important;
            font-size: 8px !important;
          }
          
          /* الأرقام بخط عريض */
          table tbody td[align="right"] {
            font-weight: 600 !important;
          }
          
          /* Chips للطباعة */
          .MuiChip-root { 
            height: auto !important;
            padding: 2px 8px !important;
            font-size: 8px !important;
            border: 1px solid #000000 !important;
            background: white !important;
            color: #000000 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .MuiChip-colorPrimary {
            border-color: #3F51B5 !important;
            background: #E8EAF6 !important;
          }
          
          .MuiChip-colorSecondary {
            border-color: #9C27B0 !important;
            background: #F3E5F5 !important;
          }
          
          /* Container */
          .MuiTableContainer-root { 
            box-shadow: none !important;
            border-radius: 0 !important;
            overflow: visible !important;
            border: 2px solid #3F51B5 !important;
          }
          
          /* رأس التقرير */
          .print-only h4, 
          .print-only h3, 
          .print-only h5 { 
            color: #000000 !important;
            font-weight: bold !important;
          }
          
          .print-only .MuiPaper-root { 
            box-shadow: none !important;
            background: white !important;
          }
          
          .print-only img { 
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          /* شريط العنوان */
          .print-only > div:nth-child(2) {
            background: #3F51B5 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            padding: 8px !important;
          }
          
          .print-only > div:nth-child(2) > div {
            color: white !important;
          }
          
          /* الخط الفاصل */
          .print-only > div:first-child {
            border-bottom: 3px solid #3F51B5 !important;
            padding-bottom: 10px !important;
            margin-bottom: 10px !important;
          }
          
          /* معلومات الإحصائيات */
          .print-only > div:nth-child(3) {
            border: 2px solid #3F51B5 !important;
            padding: 8px !important;
            margin-bottom: 10px !important;
            background: #F5F5F5 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        
        @media screen { 
          .print-only { display: none !important; } 
        }
      `}</style>
    </Box>
  );
};

export default InventoryValuationReport;